import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Workspace, BrandInfo } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import { Input, InputWrapper, Label } from '../components/Input';
import { PageSkeleton } from '../components/PageSkeleton';
import { formatJsonField } from '../utils/formatBrandData';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { useAssetManagement, useBackgroundImageManagement } from '../hooks/workspacePage';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const AssetItem = styled.div<{ $isLogo?: boolean }>`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: ${({ theme, $isLogo }) => $isLogo ? `2px solid ${theme.colors.primary}` : theme.borders.medium};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${AssetItem}:hover & {
    opacity: 1;
  }
`;

const LogoBadge = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 2px 6px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.5px;
  z-index: 5;
`;

const SetLogoButton = styled.button`
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  padding: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 11px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 5;

  &:hover {
    background-color: rgba(0, 0, 0, 0.85);
  }

  ${AssetItem}:hover & {
    opacity: 1;
  }
`;

const LogoScaleSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const LogoScaleLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const LogoScaleValue = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const LogoScaleSlider = styled.input`
  width: 100%;
  height: 6px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: none;
  }
`;

const LogoScaleHint = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const InfoItem = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-line;
  line-height: 1.5;
`;

const AddAssetForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const EmptyAssets = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const ChecklistCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const ChecklistTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ChecklistSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ChecklistProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ChecklistProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary || theme.colors.primary});
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ChecklistSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

const ChecklistStep = styled.div<{ $done: boolean; $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: 8px;
  border: 1px solid ${({ theme, $done }) => $done ? theme.colors.success + '40' : theme.colors.border};
  background: ${({ theme, $done }) => $done ? theme.colors.success + '10' : theme.colors.background};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${({ $clickable, theme }) => $clickable && `
    &:hover {
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.sm};
      border-color: ${theme.colors.primary};
    }
  `}
`;

const ChecklistIcon = styled.div<{ $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
  background: ${({ theme, $done }) => $done ? theme.colors.success : theme.colors.surfaceHover};
  color: ${({ theme, $done }) => $done ? 'white' : theme.colors.textMuted};
  border: 2px solid ${({ theme, $done }) => $done ? theme.colors.success : theme.colors.border};
`;

const ChecklistStepText = styled.div`
  flex: 1;
`;

const ChecklistStepTitle = styled.div<{ $done: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme, $done }) => $done ? theme.colors.textSecondary : theme.colors.text};
  text-decoration: ${({ $done }) => $done ? 'line-through' : 'none'};
`;

const ChecklistStepDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
`;

const AutoFillBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}15, ${({ theme }) => theme.colors.secondary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}40;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;

const AutoFillText = styled.div`
  flex: 1;
  min-width: 200px;
`;

const AutoFillTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const AutoFillDesc = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.surfaceHover};
  border: ${({ theme }) => theme.borders.thin};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ClearFileButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  padding: 4px;
  font-size: 16px;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const ScrapedImagesSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const ScrapedImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ScrapedImageItem = styled.div<{ $selected: boolean }>`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.colors.primary : 'transparent')};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme, $selected }) => ($selected ? theme.colors.primary : theme.colors.border)};
    transform: scale(1.02);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SelectBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  border: ${({ theme }) => theme.borders.thin};
`;

const ScrapedImagesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ScrapedImagesTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const SelectionInfo = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 450px;
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.error};
`;

const ModalMessage = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
`;

interface ScrapeResponse extends BrandInfo {
  scrapedImages?: string[];
}

export function WorkspacePage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const { removeWorkspace } = useWorkspaces();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [isAnalyzingDocument, setIsAnalyzingDocument] = useState(false);
  const [scrapedImages, setScrapedImages] = useState<string[]>([]);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isAddingSelectedImages, setIsAddingSelectedImages] = useState(false);
  const [imageScrapeUrl, setImageScrapeUrl] = useState('');
  const [isScrapingImages, setIsScrapingImages] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    assets, assetUrl, setAssetUrl, assetName, setAssetName,
    isAddingAsset, assetFiles, setAssetFiles,
    isUploadingAsset, uploadProgress, uploadErrors,
    fetchAssets, handleAddAsset, handleDeleteAsset, handleSetLogo,
    handleAssetFileUpload, handleAddSelectedImages: addSelectedAsAssets,
  } = useAssetManagement(workspaceId);

  const {
    backgroundImages, bgImageFiles, setBgImageFiles,
    isUploadingBgImage, bgUploadProgress, bgUploadErrors,
    bgImageUrl, setBgImageUrl, bgImageName, setBgImageName,
    isAddingBgImage, isAddingSelectedAsBackgrounds,
    fetchBackgroundImages, handleBackgroundImageUpload,
    handleDeleteBackgroundImage, handleAddBackgroundImageUrl,
    handleAddSelectedAsBackgrounds: addSelectedAsBgs,
  } = useBackgroundImageManagement(workspaceId);

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace();
      fetchAssets();
      fetchBrandInfo();
      fetchBackgroundImages();
    }
  }, [workspaceId]);

  const fetchWorkspace = async () => {
    try {
      const data = await apiClient.get<Workspace>(`/api/workspaces/${workspaceId}`);
      setWorkspace(data);
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
    }
  };

  const fetchBrandInfo = async () => {
    try {
      const data = await apiClient.get<BrandInfo>(`/api/workspaces/${workspaceId}/brand-info`);
      setBrandInfo(data);
    } catch (error) {
      console.error('Failed to fetch brand info:', error);
    }
  };

  const handleScrape = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsScraping(true);
    setScrapedImages([]);
    setSelectedImages(new Set());

    try {
      const data = await apiClient.post<ScrapeResponse>(`/api/workspaces/${workspaceId}/scrape`, {
        url: scrapeUrl,
      });
      setBrandInfo(data);
      setScrapeUrl('');
      // Store scraped images for selection
      if (data.scrapedImages && data.scrapedImages.length > 0) {
        setScrapedImages(data.scrapedImages);
      }
    } catch (error) {
      console.error('Failed to scrape:', error);
    } finally {
      setIsScraping(false);
    }
  };

  const toggleImageSelection = (imageUrl: string) => {
    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageUrl)) {
        newSet.delete(imageUrl);
      } else {
        newSet.add(imageUrl);
      }
      return newSet;
    });
  };

  const handleScrapeImagesOnly = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageScrapeUrl) return;

    setIsScrapingImages(true);
    setScrapedImages([]);
    setSelectedImages(new Set());

    try {
      const data = await apiClient.post<{ images: string[]; url: string }>(
        `/api/workspaces/${workspaceId}/scrape-images`,
        { url: imageScrapeUrl }
      );
      if (data.images && data.images.length > 0) {
        setScrapedImages(data.images);
      }
      setImageScrapeUrl('');
    } catch (error) {
      console.error('Failed to scrape images:', error);
    } finally {
      setIsScrapingImages(false);
    }
  };

  const handleAddSelectedImages = async () => {
    if (selectedImages.size === 0) return;
    setIsAddingSelectedImages(true);
    try {
      await addSelectedAsAssets(selectedImages);
      setSelectedImages(new Set());
      setScrapedImages([]);
    } catch (error) {
      console.error('Failed to add selected images:', error);
    } finally {
      setIsAddingSelectedImages(false);
    }
  };

  const handleLogoScaleChange = async (scale: number) => {
    if (!workspaceId) return;
    setWorkspace(prev => prev ? { ...prev, logoScale: scale } : prev);
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/logo-scale`, { logoScale: scale });
    } catch (error) {
      console.error('Failed to update logo scale:', error);
    }
  };

  const handleLogoOffsetChange = async (offsetX: number, offsetY: number) => {
    if (!workspaceId) return;
    setWorkspace(prev => prev ? { ...prev, logoOffsetX: offsetX, logoOffsetY: offsetY } : prev);
    try {
      await apiClient.patch(`/api/workspaces/${workspaceId}/logo-scale`, { logoOffsetX: offsetX, logoOffsetY: offsetY });
    } catch (error) {
      console.error('Failed to update logo offset:', error);
    }
  };

  const handleDocumentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentFile) return;

    setIsAnalyzingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentFile);

      const data = await apiClient.upload<BrandInfo>(
        `/api/workspaces/${workspaceId}/analyze-document`,
        formData
      );
      setBrandInfo(data);
      setDocumentFile(null);
    } catch (error) {
      console.error('Failed to analyze document:', error);
    } finally {
      setIsAnalyzingDocument(false);
    }
  };

  const handleAddSelectedAsBackgrounds = async () => {
    if (selectedImages.size === 0) return;
    await addSelectedAsBgs(selectedImages, () => {
      setSelectedImages(new Set());
      setScrapedImages([]);
    });
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;

    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}`);
      removeWorkspace(workspaceId);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      alert('Failed to delete workspace. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (!workspace) {
    return <PageSkeleton />;
  }

  return (
    <>
      <PageHeader>
        <Title>{workspace.name}</Title>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/brand-profile`)}>
            Brand Profile
          </Button>
          <Button $variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/templates`)}>
            Templates
          </Button>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/generate`)}>
            Generate Content
          </Button>
          <Button
            $variant="outline"
            onClick={() => navigate(`/workspace/${workspaceId}/generate?quickgen=true`)}
            title="Generate 2 posts per platform with smart defaults"
          >
            Quick Generate
          </Button>
          <Button $variant="outline" onClick={() => setShowDeleteModal(true)} style={{ color: '#ef4444' }}>
            Delete Workspace
          </Button>
        </div>
      </PageHeader>

      {/* Getting Started Checklist */}
      {(() => {
        const hasBrandInfo = !!(brandInfo?.description || brandInfo?.businessOverview);
        const hasAssets = assets.length > 0;
        const hasLogo = assets.some(a => a.isLogo);
        const hasPosts = (workspace.postsCount || 0) > 0;
        const completedSteps = [hasBrandInfo, hasAssets || hasLogo, hasPosts].filter(Boolean).length;
        const totalSteps = 3;
        const allDone = completedSteps === totalSteps;

        if (!allDone) {
          return (
            <ChecklistCard>
              <ChecklistTitle>Getting Started</ChecklistTitle>
              <ChecklistSubtitle>Complete these steps to generate your first content</ChecklistSubtitle>
              <ChecklistProgressBar>
                <ChecklistProgressFill $progress={(completedSteps / totalSteps) * 100} />
              </ChecklistProgressBar>
              <ChecklistSteps>
                <ChecklistStep $done={hasBrandInfo} $clickable={!hasBrandInfo} onClick={() => {
                  if (!hasBrandInfo) {
                    const el = document.getElementById('scrapeUrl');
                    el?.focus();
                  }
                }}>
                  <ChecklistIcon $done={hasBrandInfo}>{hasBrandInfo ? '\u2713' : '1'}</ChecklistIcon>
                  <ChecklistStepText>
                    <ChecklistStepTitle $done={hasBrandInfo}>Analyze your brand</ChecklistStepTitle>
                    <ChecklistStepDesc>Paste a website URL or upload a document below</ChecklistStepDesc>
                  </ChecklistStepText>
                </ChecklistStep>
                <ChecklistStep $done={hasAssets || hasLogo} $clickable={!hasAssets && !hasLogo}>
                  <ChecklistIcon $done={hasAssets || hasLogo}>{hasAssets || hasLogo ? '\u2713' : '2'}</ChecklistIcon>
                  <ChecklistStepText>
                    <ChecklistStepTitle $done={hasAssets || hasLogo}>Add brand assets</ChecklistStepTitle>
                    <ChecklistStepDesc>Upload your logo and images for templates</ChecklistStepDesc>
                  </ChecklistStepText>
                </ChecklistStep>
                <ChecklistStep $done={hasPosts} $clickable={hasBrandInfo && !hasPosts} onClick={() => {
                  if (hasBrandInfo && !hasPosts) navigate(`/workspace/${workspaceId}/generate`);
                }}>
                  <ChecklistIcon $done={hasPosts}>{hasPosts ? '\u2713' : '3'}</ChecklistIcon>
                  <ChecklistStepText>
                    <ChecklistStepTitle $done={hasPosts}>Generate your first posts</ChecklistStepTitle>
                    <ChecklistStepDesc>Create AI-powered content for social media</ChecklistStepDesc>
                  </ChecklistStepText>
                </ChecklistStep>
              </ChecklistSteps>
            </ChecklistCard>
          );
        }
        return null;
      })()}

      <Grid>
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Research</CardTitle>
              <CardDescription>Scrape websites to gather brand information</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleScrape}>
              <InputWrapper>
                <Label htmlFor="scrapeUrl">Website URL</Label>
                <Input
                  id="scrapeUrl"
                  type="url"
                  value={scrapeUrl}
                  onChange={(e) => setScrapeUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </InputWrapper>
              <Button type="submit" $fullWidth disabled={isScraping || !scrapeUrl} style={{ marginTop: 16 }}>
                {isScraping ? 'Analyzing...' : 'Analyze Website'}
              </Button>
            </form>

            <OrDivider>or upload a document</OrDivider>

            <form onSubmit={handleDocumentUpload}>
              <FileInput
                type="file"
                id="documentFile"
                accept=".txt,.docx,.pdf"
                onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
              />
              {!documentFile ? (
                <FileInputLabel htmlFor="documentFile">
                  Click to upload TXT, DOCX, or PDF
                </FileInputLabel>
              ) : (
                <SelectedFile>
                  <span>{documentFile.name}</span>
                  <ClearFileButton type="button" onClick={() => setDocumentFile(null)}>
                    ×
                  </ClearFileButton>
                </SelectedFile>
              )}
              <Button
                type="submit"
                $fullWidth
                disabled={isAnalyzingDocument || !documentFile}
                style={{ marginTop: 16 }}
              >
                {isAnalyzingDocument ? 'Analyzing...' : 'Analyze Document'}
              </Button>
            </form>

            {brandInfo && (
              <div style={{ marginTop: 24 }}>
                <SectionTitle>Brand Analysis</SectionTitle>
                {brandInfo.description && (
                  <InfoItem>
                    <InfoLabel>Description</InfoLabel>
                    <InfoValue>{formatJsonField(brandInfo.description)}</InfoValue>
                  </InfoItem>
                )}
                {brandInfo.targetAudience && (
                  <InfoItem>
                    <InfoLabel>Target Audience</InfoLabel>
                    <InfoValue>{formatJsonField(brandInfo.targetAudience)}</InfoValue>
                  </InfoItem>
                )}
                {brandInfo.tone && (
                  <InfoItem>
                    <InfoLabel>Brand Tone</InfoLabel>
                    <InfoValue>{formatJsonField(brandInfo.tone)}</InfoValue>
                  </InfoItem>
                )}
                {brandInfo.keywords && brandInfo.keywords.length > 0 && (
                  <InfoItem>
                    <InfoLabel>Keywords</InfoLabel>
                    <InfoValue>{brandInfo.keywords.join(', ')}</InfoValue>
                  </InfoItem>
                )}
                <AutoFillBanner>
                  <AutoFillText>
                    <AutoFillTitle>Brand Profile auto-populated</AutoFillTitle>
                    <AutoFillDesc>We've filled in your brand profile from this analysis. Review and refine it for better results.</AutoFillDesc>
                  </AutoFillText>
                  <Button onClick={() => navigate(`/workspace/${workspaceId}/brand-profile`)}>
                    Review Brand Profile
                  </Button>
                </AutoFillBanner>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Assets</CardTitle>
              <CardDescription>Upload images or add URLs for your brand</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAssetFileUpload}>
              <FileInput
                type="file"
                id="assetFile"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 10) {
                    alert('Max 10 images at a time. Please select up to 10 files.');
                    e.target.value = '';
                    return;
                  }
                  setAssetFiles(files);
                }}
              />
              {assetFiles.length === 0 ? (
                <FileInputLabel htmlFor="assetFile">
                  Click to upload images (multiple allowed)
                </FileInputLabel>
              ) : (
                <SelectedFile>
                  <span>{assetFiles.length} file{assetFiles.length > 1 ? 's' : ''} selected</span>
                  <ClearFileButton type="button" onClick={() => setAssetFiles([])}>
                    ×
                  </ClearFileButton>
                </SelectedFile>
              )}
              <Button
                type="submit"
                $fullWidth
                disabled={isUploadingAsset || assetFiles.length === 0}
                style={{ marginTop: 16 }}
              >
                {isUploadingAsset && uploadProgress
                  ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
                  : `Upload Image${assetFiles.length > 1 ? 's' : ''}`}
              </Button>
              {uploadErrors.length > 0 && (
                <div style={{ marginTop: 8, color: '#e53e3e', fontSize: 12 }}>
                  Failed to upload: {uploadErrors.join(', ')}. Try uploading fewer files or paste a URL instead.
                </div>
              )}
            </form>

            <OrDivider>or add image URL</OrDivider>

            <AddAssetForm onSubmit={handleAddAsset}>
              <InputWrapper>
                <Label htmlFor="assetName">Asset Name</Label>
                <Input
                  id="assetName"
                  type="text"
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  placeholder="e.g., Main Logo"
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="assetUrl">Image URL</Label>
                <Input
                  id="assetUrl"
                  type="url"
                  value={assetUrl}
                  onChange={(e) => setAssetUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </InputWrapper>
              <Button type="submit" $fullWidth disabled={isAddingAsset || !assetUrl || !assetName}>
                {isAddingAsset ? 'Adding...' : 'Add Asset'}
              </Button>
            </AddAssetForm>

            <OrDivider>or analyze web for images</OrDivider>

            <AddAssetForm onSubmit={handleScrapeImagesOnly}>
              <InputWrapper>
                <Label htmlFor="imageScrapeUrl">Website URL</Label>
                <Input
                  id="imageScrapeUrl"
                  type="url"
                  value={imageScrapeUrl}
                  onChange={(e) => setImageScrapeUrl(e.target.value)}
                  placeholder="https://example.com"
                />
              </InputWrapper>
              <Button type="submit" $fullWidth disabled={isScrapingImages || !imageScrapeUrl}>
                {isScrapingImages ? 'Analyzing...' : 'Find Images on Website'}
              </Button>
            </AddAssetForm>

            {assets.length > 0 ? (
              <AssetGrid>
                {assets.map((asset) => (
                  <AssetItem key={asset.id} $isLogo={asset.isLogo}>
                    <img src={asset.url} alt={asset.name} />
                    {asset.isLogo && <LogoBadge>Logo</LogoBadge>}
                    {!asset.isLogo && (
                      <SetLogoButton onClick={() => handleSetLogo(asset.id)}>
                        Set as Logo
                      </SetLogoButton>
                    )}
                    <DeleteButton onClick={() => handleDeleteAsset(asset.id)}>×</DeleteButton>
                  </AssetItem>
                ))}
              </AssetGrid>
            ) : (
              <EmptyAssets>No assets added yet. Add image URLs above.</EmptyAssets>
            )}

            {assets.some(a => a.isLogo) && (
              <LogoScaleSection>
                <LogoScaleLabel>
                  Logo Size: <LogoScaleValue>{((workspace?.logoScale ?? 1.0) * 100).toFixed(0)}%</LogoScaleValue>
                </LogoScaleLabel>
                <LogoScaleSlider
                  type="range"
                  min="0.3"
                  max="3.0"
                  step="0.1"
                  value={workspace?.logoScale ?? 1.0}
                  onChange={(e) => handleLogoScaleChange(parseFloat(e.target.value))}
                />
                <LogoScaleHint>Adjust logo size in generated post images</LogoScaleHint>

                <LogoScaleLabel style={{ marginTop: 12 }}>
                  Horizontal Offset: <LogoScaleValue>{(workspace?.logoOffsetX ?? 0).toFixed(0)}%</LogoScaleValue>
                </LogoScaleLabel>
                <LogoScaleSlider
                  type="range"
                  min="-50"
                  max="50"
                  step="1"
                  value={workspace?.logoOffsetX ?? 0}
                  onChange={(e) => handleLogoOffsetChange(parseFloat(e.target.value), workspace?.logoOffsetY ?? 0)}
                />

                <LogoScaleLabel style={{ marginTop: 8 }}>
                  Vertical Offset: <LogoScaleValue>{(workspace?.logoOffsetY ?? 0).toFixed(0)}%</LogoScaleValue>
                </LogoScaleLabel>
                <LogoScaleSlider
                  type="range"
                  min="-50"
                  max="50"
                  step="1"
                  value={workspace?.logoOffsetY ?? 0}
                  onChange={(e) => handleLogoOffsetChange(workspace?.logoOffsetX ?? 0, parseFloat(e.target.value))}
                />
                <LogoScaleHint>Shift logo position across all templates</LogoScaleHint>
              </LogoScaleSection>
            )}

            {scrapedImages.length > 0 && (
              <ScrapedImagesSection>
                <ScrapedImagesHeader>
                  <ScrapedImagesTitle>Images Found on Website</ScrapedImagesTitle>
                  <SelectionInfo>{selectedImages.size} selected</SelectionInfo>
                </ScrapedImagesHeader>
                <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 12 }}>
                  Click images to select them, then add as brand assets.
                </p>
                <ScrapedImagesGrid>
                  {scrapedImages.map((imageUrl, index) => (
                    <ScrapedImageItem
                      key={index}
                      $selected={selectedImages.has(imageUrl)}
                      onClick={() => toggleImageSelection(imageUrl)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Scraped ${index + 1}`}
                        onError={(e) => {
                          // Hide broken images
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      {selectedImages.has(imageUrl) && <SelectBadge>✓</SelectBadge>}
                    </ScrapedImageItem>
                  ))}
                </ScrapedImagesGrid>
                <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button
                    onClick={handleAddSelectedImages}
                    disabled={selectedImages.size === 0 || isAddingSelectedImages || isAddingSelectedAsBackgrounds}
                  >
                    {isAddingSelectedImages
                      ? 'Adding...'
                      : `Add ${selectedImages.size} Selected Image${selectedImages.size !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    $variant="outline"
                    onClick={handleAddSelectedAsBackgrounds}
                    disabled={selectedImages.size === 0 || isAddingSelectedImages || isAddingSelectedAsBackgrounds}
                  >
                    {isAddingSelectedAsBackgrounds
                      ? 'Adding...'
                      : `Add as Background${selectedImages.size !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    $variant="outline"
                    onClick={() => {
                      setScrapedImages([]);
                      setSelectedImages(new Set());
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </ScrapedImagesSection>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Background Images</CardTitle>
              <CardDescription>Upload photos to use as post backgrounds</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBackgroundImageUpload}>
              <FileInput
                type="file"
                id="bgImageFile"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 10) {
                    alert('Max 10 images at a time. Please select up to 10 files.');
                    e.target.value = '';
                    return;
                  }
                  setBgImageFiles(files);
                }}
              />
              {bgImageFiles.length === 0 ? (
                <FileInputLabel htmlFor="bgImageFile">
                  Click to upload photos (multiple allowed)
                </FileInputLabel>
              ) : (
                <SelectedFile>
                  <span>{bgImageFiles.length} file{bgImageFiles.length > 1 ? 's' : ''} selected</span>
                  <ClearFileButton type="button" onClick={() => setBgImageFiles([])}>
                    ×
                  </ClearFileButton>
                </SelectedFile>
              )}
              <Button
                type="submit"
                $fullWidth
                disabled={isUploadingBgImage || bgImageFiles.length === 0}
                style={{ marginTop: 16 }}
              >
                {isUploadingBgImage && bgUploadProgress
                  ? `Uploading ${bgUploadProgress.current}/${bgUploadProgress.total}...`
                  : `Upload Photo${bgImageFiles.length > 1 ? 's' : ''}`}
              </Button>
              {bgUploadErrors.length > 0 && (
                <div style={{ marginTop: 8, color: '#e53e3e', fontSize: 12 }}>
                  Failed to upload: {bgUploadErrors.join(', ')}. Try uploading fewer files or paste a URL instead.
                </div>
              )}
            </form>

            <OrDivider>or add image URL</OrDivider>

            <AddAssetForm onSubmit={handleAddBackgroundImageUrl}>
              <InputWrapper>
                <Label htmlFor="bgImageName">Image Name</Label>
                <Input
                  id="bgImageName"
                  type="text"
                  value={bgImageName}
                  onChange={(e) => setBgImageName(e.target.value)}
                  placeholder="e.g., Office Photo"
                />
              </InputWrapper>
              <InputWrapper>
                <Label htmlFor="bgImageUrl">Image URL</Label>
                <Input
                  id="bgImageUrl"
                  type="url"
                  value={bgImageUrl}
                  onChange={(e) => setBgImageUrl(e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                />
              </InputWrapper>
              <Button type="submit" $fullWidth disabled={isAddingBgImage || !bgImageUrl || !bgImageName}>
                {isAddingBgImage ? 'Adding...' : 'Add Background Image'}
              </Button>
            </AddAssetForm>

            {backgroundImages.length > 0 ? (
              <AssetGrid style={{ marginTop: 16 }}>
                {backgroundImages.map((image) => (
                  <AssetItem key={image.id}>
                    <img src={image.url} alt={image.name} />
                    <DeleteButton onClick={() => handleDeleteBackgroundImage(image.id)}>×</DeleteButton>
                  </AssetItem>
                ))}
              </AssetGrid>
            ) : (
              <EmptyAssets>No background images yet. Upload photos or add URLs above to use as post backgrounds.</EmptyAssets>
            )}
          </CardContent>
        </Card>
      </Grid>

      {showDeleteModal && (
        <Modal onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Delete Workspace?</ModalTitle>
            <ModalMessage>
              Are you sure you want to delete &quot;{workspace.name}&quot;? This will permanently delete:
              <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
                <li>All brand information and research</li>
                <li>All brand assets and images</li>
                <li>All generated posts ({workspace.postsCount || 0} posts)</li>
                <li>All export history</li>
              </ul>
              <strong style={{ display: 'block', marginTop: '12px', color: '#ef4444' }}>
                This action cannot be undone.
              </strong>
            </ModalMessage>
            <ModalActions>
              <Button type="button" $variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteWorkspace}
                disabled={isDeleting}
                style={{ backgroundColor: '#ef4444' }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Workspace'}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
